import { useCallback, useEffect, useMemo, useRef } from 'react';
import chunk from 'lodash/chunk';

import { postSpotRecommendationsSeen } from 'common/api/spotRecommendations';

const DWELL_MS = 1000;
const FLUSH_INTERVAL_MS = 5000;
const MAX_IDS_PER_REQUEST = 50;

interface UseSeenTrackerOptions {
  enabled: boolean;
}

interface UseSeenTrackerResult {
  /** Start a 1s dwell timer for a recommendation card that has just become visible. */
  register: (spotId: string) => void;
  /** Cancel the dwell timer for a card that left the viewport before the threshold. */
  unregister: (spotId: string) => void;
  /** Remove a spot from the pending POST batch (e.g. user favorited it before flush). */
  cancel: (spotId: string) => void;
}

/**
 * Tracks recommendation card views with a 1s dwell threshold. Cards that stay
 * visible for at least DWELL_MS are batched and POSTed to the API every
 * FLUSH_INTERVAL_MS (max MAX_IDS_PER_REQUEST per request). Also flushes on
 * page visibility change.
 */
const useSeenTracker = ({ enabled }: UseSeenTrackerOptions): UseSeenTrackerResult => {
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const pendingRef = useRef<Array<string>>([]);
  const sessionSeenRef = useRef<Set<string>>(new Set());
  const enabledRef = useRef(enabled);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const flush = useCallback(() => {
    if (!enabledRef.current) return;
    if (pendingRef.current.length === 0) return;
    const batch = pendingRef.current;
    pendingRef.current = [];
    chunk(batch, MAX_IDS_PER_REQUEST).forEach((ids) => {
      postSpotRecommendationsSeen(ids).catch(() => {});
    });
  }, []);

  const register = useCallback((spotId: string) => {
    if (!enabledRef.current) return;
    if (sessionSeenRef.current.has(spotId)) return;
    if (timersRef.current.has(spotId)) return;
    const timer = setTimeout(() => {
      timersRef.current.delete(spotId);
      if (sessionSeenRef.current.has(spotId)) return;
      sessionSeenRef.current.add(spotId);
      pendingRef.current.push(spotId);
    }, DWELL_MS);
    timersRef.current.set(spotId, timer);
  }, []);

  const unregister = useCallback((spotId: string) => {
    const timer = timersRef.current.get(spotId);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(spotId);
    }
  }, []);

  const cancel = useCallback(
    (spotId: string) => {
      unregister(spotId);
      pendingRef.current = pendingRef.current.filter((id) => id !== spotId);
    },
    [unregister],
  );

  useEffect(() => {
    if (!enabled) return undefined;

    const timers = timersRef.current;
    const interval = setInterval(flush, FLUSH_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
      flush();
    };
  }, [enabled, flush]);

  return useMemo(() => ({ register, unregister, cancel }), [register, unregister, cancel]);
};

export default useSeenTracker;
